import React, { useState, useRef } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Box, Typography, Grid, CircularProgress,
    IconButton, TextField, Switch, FormControlLabel,
    MenuItem, Avatar,
} from '@mui/material';
import {
    Close as CloseIcon,
    PhotoCamera as PhotoCameraIcon,
    Person as PersonIcon,
    DirectionsCar as CarIcon,
} from '@mui/icons-material';
import { adminAPI } from '../services/adminService';
import { driversAPI, dispatchersAPI } from '../services/apiService';
import { useAlert } from '../contexts/AlertContext';
import { formatPhoneNumber } from '../config/appConstants';

const GENDER_OPTIONS = ['Male', 'Female', 'Non-binary', 'Other', 'Prefer not to say'];

const SectionTitle = ({ children }) => (
    <Typography variant="subtitle2" fontWeight={700} color="primary" sx={{ mt: 2, mb: 0.5 }}>
        {children}
    </Typography>
);

const EF = ({ label, value, onChange, type = 'text', required, error, inputProps, InputLabelProps, select, children, multiline, rows, size = 'small' }) => (
    <TextField
        fullWidth
        label={label}
        value={value ?? ''}
        onChange={e => onChange(e.target.value)}
        type={type}
        required={required}
        error={!!error}
        helperText={error || ''}
        size={size}
        margin="dense"
        inputProps={inputProps}
        InputLabelProps={InputLabelProps}
        select={select}
        multiline={multiline}
        rows={rows}
    >
        {children}
    </TextField>
);

// ─── Photo picker UI ──────────────────────────────────────────────────────────
const PhotoPicker = ({ photoFile, photoPreview, onSelect, onClear }) => {
    const inputRef = useRef(null);
    return (
        <Box display="flex" flexDirection="column" alignItems="center" gap={1} sx={{ mt: 1, mb: 1 }}>
            <Avatar
                src={photoPreview}
                sx={{ width: 80, height: 80, border: '2px solid', borderColor: photoPreview ? 'primary.main' : 'divider', cursor: 'pointer' }}
                onClick={() => inputRef.current?.click()}
            >
                {!photoPreview && <PersonIcon sx={{ fontSize: 36 }} />}
            </Avatar>
            <Box display="flex" gap={1}>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PhotoCameraIcon />}
                    onClick={() => inputRef.current?.click()}
                >
                    {photoPreview ? 'Change Photo' : 'Add Photo'}
                </Button>
                {photoPreview && (
                    <Button size="small" color="error" variant="outlined" onClick={onClear}>
                        Remove
                    </Button>
                )}
            </Box>
            <input
                ref={inputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                style={{ display: 'none' }}
                onChange={e => {
                    const file = e.target.files?.[0];
                    if (file) onSelect(file);
                    e.target.value = '';
                }}
            />
        </Box>
    );
};

// ═════════════════════════════════════════════════════════════════════════════
// Main component
// ═════════════════════════════════════════════════════════════════════════════
export default function AddEmployeeDialog({ open, employeeType, onClose, onSuccess }) {
    const { showAlert, showToast } = useAlert();
    const phoneInputRef = useRef(null);
    const [submitting, setSubmitting] = useState(false);

    // Photo state
    const [photoFile, setPhotoFile] = useState(null);
    const [photoPreview, setPhotoPreview] = useState(null);

    // ── Driver form state ────────────────────────────────────────────────────
    const [driverData, setDriverData] = useState({
        name: '', email: '', phoneNumber: '',
        licenseNumber: '', userName: '',
        dateOfBirth: '', gender: '',
        addressStreet: '', addressCity: '', addressState: '', addressZip: '',
        emergencyContactName: '', emergencyContactPhone: '',
        licenseState: '', licenseExpiration: '',
        yearsDrivingExperience: '', languagesSpoken: '', preferredWorkHours: '',
        insuranceCarrier: '', insurancePolicyNumber: '', insuranceExpiration: '',
    });

    // ── Dispatcher form state ────────────────────────────────────────────────
    const [dispatcherData, setDispatcherData] = useState({
        name: '', email: '', phoneNumber: '',
        isActive: true, isAdmin: false,
    });

    const [errors, setErrors] = useState({});

    // ── Helpers ──────────────────────────────────────────────────────────────
    const handlePhotoSelect = (file) => {
        setPhotoFile(file);
        const url = URL.createObjectURL(file);
        setPhotoPreview(url);
    };

    const handlePhotoClear = () => {
        setPhotoFile(null);
        if (photoPreview) URL.revokeObjectURL(photoPreview);
        setPhotoPreview(null);
    };

    const changeDriver = (field, value) => {
        let processed = value;
        if (field === 'phoneNumber') {
            processed = formatPhoneNumber(value.replace(/\D/g, ''));
        }
        setDriverData(prev => ({ ...prev, [field]: processed }));
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    };

    const changeDispatcher = (field, value) => {
        let processed = value;
        if (field === 'phoneNumber') {
            processed = formatPhoneNumber(value.replace(/\D/g, ''));
        }
        setDispatcherData(prev => ({ ...prev, [field]: processed }));
        if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    };

    const validateDriver = () => {
        const e = {};
        if (!driverData.name.trim()) e.name = 'Required';
        if (!driverData.email.trim()) e.email = 'Required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(driverData.email)) e.email = 'Invalid email';
        const digits = driverData.phoneNumber.replace(/\D/g, '');
        if (digits.length !== 10) e.phoneNumber = 'Valid 10-digit phone required';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const validateDispatcher = () => {
        const e = {};
        if (!dispatcherData.name.trim()) e.name = 'Required';
        if (!dispatcherData.email.trim()) e.email = 'Required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(dispatcherData.email)) e.email = 'Invalid email';
        const digits = dispatcherData.phoneNumber.replace(/\D/g, '');
        if (digits.length !== 10) e.phoneNumber = 'Valid 10-digit phone required';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleClose = () => {
        if (submitting) return;
        resetState();
        onClose();
    };

    const resetState = () => {
        setDriverData({
            name: '', email: '', phoneNumber: '',
            licenseNumber: '', userName: '',
            dateOfBirth: '', gender: '',
            addressStreet: '', addressCity: '', addressState: '', addressZip: '',
            emergencyContactName: '', emergencyContactPhone: '',
            licenseState: '', licenseExpiration: '',
            yearsDrivingExperience: '', languagesSpoken: '', preferredWorkHours: '',
            insuranceCarrier: '', insurancePolicyNumber: '', insuranceExpiration: '',
        });
        setDispatcherData({ name: '', email: '', phoneNumber: '', isActive: true, isAdmin: false });
        setErrors({});
        handlePhotoClear();
    };

    // ── Submit ───────────────────────────────────────────────────────────────
    const handleSubmit = async () => {
        if (employeeType === 'driver') {
            if (!validateDriver()) return;
            setSubmitting(true);
            try {
                const payload = {
                    ...driverData,
                    password: '',
                    dateOfBirth: driverData.dateOfBirth || null,
                    licenseExpiration: driverData.licenseExpiration || null,
                    insuranceExpiration: driverData.insuranceExpiration || null,
                    yearsDrivingExperience: driverData.yearsDrivingExperience !== '' && driverData.yearsDrivingExperience != null
                        ? Number(driverData.yearsDrivingExperience)
                        : null,
                };
                const response = await adminAPI.drivers.create(payload);

                // Upload photo if one was selected
                if (photoFile && response?.newId) {
                    try {
                        await driversAPI.uploadDocument(response.newId, 'ProfilePhoto', photoFile);
                    } catch {
                        // Non-fatal: driver was created, just warn
                        showToast('Driver created, but photo upload failed.', 'warning');
                    }
                }

                if (response?.warning) {
                    showAlert(
                        'Email delivery issue',
                        `Driver created, but the welcome email could not be sent.\n\nTemporary password: ${response.tempPassword}`,
                        [{ text: 'OK' }],
                        'warning'
                    );
                } else {
                    showToast('Driver added successfully. Welcome email sent.', 'success');
                }

                onSuccess?.();
                handleClose();
            } catch (err) {
                const msg = err?.response?.data?.error;
                if (msg?.toLowerCase().includes('username')) {
                    setErrors(prev => ({ ...prev, userName: msg }));
                } else {
                    showAlert('Error', 'Failed to create driver. Please try again.', [{ text: 'OK' }], 'error');
                }
            } finally {
                setSubmitting(false);
            }
        } else {
            if (!validateDispatcher()) return;
            setSubmitting(true);
            try {
                const payload = { ...dispatcherData, password: '' };
                const response = await adminAPI.dispatchers.create(payload);

                // Upload photo if one was selected
                if (photoFile && response?.newId) {
                    try {
                        await dispatchersAPI.uploadPhoto(response.newId, photoFile);
                    } catch {
                        showToast('Dispatcher created, but photo upload failed.', 'warning');
                    }
                }

                if (response?.warning) {
                    showAlert(
                        'Email delivery issue',
                        `Dispatcher created, but the welcome email could not be sent.\n\nTemporary password: ${response.tempPassword}`,
                        [{ text: 'OK' }],
                        'warning'
                    );
                } else {
                    showToast('Dispatcher added successfully. Welcome email sent.', 'success');
                }

                onSuccess?.();
                handleClose();
            } catch (err) {
                showAlert('Error', 'Failed to create dispatcher. Please try again.', [{ text: 'OK' }], 'error');
            } finally {
                setSubmitting(false);
            }
        }
    };

    // ── Render forms ─────────────────────────────────────────────────────────
    const renderDriverForm = () => (
        <Box>
            <PhotoPicker
                photoFile={photoFile}
                photoPreview={photoPreview}
                onSelect={handlePhotoSelect}
                onClear={handlePhotoClear}
            />

            <SectionTitle>Basic Information <Typography component="span" variant="caption" color="text.secondary">(required)</Typography></SectionTitle>
            <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                    <EF label="Full Name" value={driverData.name} onChange={v => changeDriver('name', v)} required error={errors.name} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <EF label="Email" value={driverData.email} onChange={v => changeDriver('email', v)} type="email" required error={errors.email} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <EF label="Phone" value={driverData.phoneNumber} onChange={v => changeDriver('phoneNumber', v)} type="tel" required error={errors.phoneNumber} inputProps={{ maxLength: 14 }} />
                </Grid>
            </Grid>

            <SectionTitle>Personal Information</SectionTitle>
            <EF label="Date of Birth" value={driverData.dateOfBirth} onChange={v => changeDriver('dateOfBirth', v)} type="date" InputLabelProps={{ shrink: true }} />
            <EF label="Gender" value={driverData.gender} onChange={v => changeDriver('gender', v)} select size="medium">
                <MenuItem value=""><em>Select…</em></MenuItem>
                {GENDER_OPTIONS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
            </EF>

            <SectionTitle>Address</SectionTitle>
            <EF label="Street" value={driverData.addressStreet} onChange={v => changeDriver('addressStreet', v)} />
            <Grid container spacing={1}>
                <Grid item xs={5}>
                    <EF label="City" value={driverData.addressCity} onChange={v => changeDriver('addressCity', v)} />
                </Grid>
                <Grid item xs={4}>
                    <EF label="State" value={driverData.addressState} onChange={v => changeDriver('addressState', v.toUpperCase())} inputProps={{ maxLength: 2 }} />
                </Grid>
                <Grid item xs={3}>
                    <EF label="Zip" value={driverData.addressZip} onChange={v => changeDriver('addressZip', v)} inputProps={{ maxLength: 10 }} />
                </Grid>
            </Grid>

            <SectionTitle>Emergency Contact</SectionTitle>
            <Grid container spacing={1}>
                <Grid item xs={6}>
                    <EF label="Name" value={driverData.emergencyContactName} onChange={v => changeDriver('emergencyContactName', v)} />
                </Grid>
                <Grid item xs={6}>
                    <EF label="Phone" value={driverData.emergencyContactPhone} onChange={v => changeDriver('emergencyContactPhone', v)} />
                </Grid>
            </Grid>

            <SectionTitle>License Information</SectionTitle>
            <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                    <EF label="License Number" value={driverData.licenseNumber} onChange={v => changeDriver('licenseNumber', v)} inputProps={{ maxLength: 15 }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <EF label="License State" value={driverData.licenseState} onChange={v => changeDriver('licenseState', v.toUpperCase())} inputProps={{ maxLength: 2 }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <EF label="License Expiration" value={driverData.licenseExpiration} onChange={v => changeDriver('licenseExpiration', v)} type="date" InputLabelProps={{ shrink: true }} />
                </Grid>
            </Grid>

            <SectionTitle>Driving Profile</SectionTitle>
            <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                    <EF label="Years Driving Experience" value={driverData.yearsDrivingExperience} onChange={v => changeDriver('yearsDrivingExperience', v)} type="number" inputProps={{ min: 0, max: 60 }} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <EF label="Languages Spoken" value={driverData.languagesSpoken} onChange={v => changeDriver('languagesSpoken', v)} />
                </Grid>
                <Grid item xs={12}>
                    <EF label="Preferred Work Hours" value={driverData.preferredWorkHours} onChange={v => changeDriver('preferredWorkHours', v)} placeholder="e.g. Weekdays 8am–6pm" />
                </Grid>
            </Grid>

            <SectionTitle>Insurance</SectionTitle>
            <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                    <EF label="Insurance Carrier" value={driverData.insuranceCarrier} onChange={v => changeDriver('insuranceCarrier', v)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <EF label="Policy Number" value={driverData.insurancePolicyNumber} onChange={v => changeDriver('insurancePolicyNumber', v)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <EF label="Insurance Expiration" value={driverData.insuranceExpiration} onChange={v => changeDriver('insuranceExpiration', v)} type="date" InputLabelProps={{ shrink: true }} />
                </Grid>
            </Grid>
        </Box>
    );

    const renderDispatcherForm = () => (
        <Box>
            <PhotoPicker
                photoFile={photoFile}
                photoPreview={photoPreview}
                onSelect={handlePhotoSelect}
                onClear={handlePhotoClear}
            />

            <SectionTitle>Basic Information <Typography component="span" variant="caption" color="text.secondary">(required)</Typography></SectionTitle>
            <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                    <EF label="Full Name" value={dispatcherData.name} onChange={v => changeDispatcher('name', v)} required error={errors.name} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <EF label="Email" value={dispatcherData.email} onChange={v => changeDispatcher('email', v)} type="email" required error={errors.email} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <EF label="Phone" value={dispatcherData.phoneNumber} onChange={v => changeDispatcher('phoneNumber', v)} type="tel" required error={errors.phoneNumber} inputProps={{ maxLength: 14 }} />
                </Grid>
            </Grid>

            <Box sx={{ mt: 1.5, display: 'flex', gap: 3 }}>
                <FormControlLabel
                    control={<Switch checked={dispatcherData.isActive} onChange={e => changeDispatcher('isActive', e.target.checked)} />}
                    label="Active"
                />
                <FormControlLabel
                    control={<Switch checked={dispatcherData.isAdmin} onChange={e => changeDispatcher('isAdmin', e.target.checked)} />}
                    label="Admin"
                />
            </Box>
        </Box>
    );

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth scroll="paper">
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box display="flex" alignItems="center" gap={1}>
                    {employeeType === 'driver' ? <CarIcon color="primary" /> : <PersonIcon color="primary" />}
                    <Typography variant="h6" fontWeight={700}>
                        Add {employeeType === 'driver' ? 'Driver' : 'Dispatcher'}
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small" disabled={submitting}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                {employeeType === 'driver' ? renderDriverForm() : renderDispatcherForm()}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={handleClose} color="inherit" disabled={submitting}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
                    {submitting ? <CircularProgress size={18} /> : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
